import React, { useMemo } from 'react';
import { BookOpen, Plus, NotebookPen, Sparkles, Trophy, Settings, ChevronDown, X } from 'lucide-react';
import { Logo } from './Logo';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Progress } from '../ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { computeLevel } from '../../lib/storage';
import { QuotaWidget } from './QuotaWidget';

const GURUS = [
  { id: 'hindi-sahitya', name: 'Hindi Sahitya Guru', desc: 'For UGC NET / TGT / PGT exam', live: true },
  { id: 'paper-1', name: 'Paper-1 Specialist', desc: 'General Studies Paper 1', live: false },
  { id: 'history', name: 'History Expert', desc: 'Ancient, Medieval & Modern', live: false },
];

export const Sidebar = ({
  chats,
  activeChatId,
  onSelectChat,
  onNewChat,
  guruId,
  onGuruChange,
  onOpenDiary,
  onOpenSettings,
  onOpenPremium,
  gami,
  onCloseMobile,
}) => {
  const guru = GURUS.find(g => g.id === guruId) || GURUS[0];
  const { level, currentXP, nextLevelXP } = useMemo(() => computeLevel(gami?.xp || 0), [gami]);
  const pct = Math.min(100, Math.round((currentXP / nextLevelXP) * 100));

  return (
    <aside
      className="h-full w-[280px] shrink-0 border-r border-border bg-card flex flex-col"
      data-testid="sidebar"
    >
      {/* Brand - clickable to start new chat */}
      <div className="px-3 pt-4 pb-2.5 flex items-center gap-2.5">
        <button
          onClick={onNewChat}
          data-testid="sidebar-brand-btn"
          className="flex items-center gap-2.5 flex-1 min-w-0 px-1.5 py-1 rounded-lg hover:bg-secondary/60 transition group"
          aria-label="Start new chat"
        >
          <Logo size="sm" />
          <div className="leading-tight text-left min-w-0">
            <div className="text-[13.5px] font-semibold tracking-tight truncate">Brahmastra <span className="text-mint">AI</span></div>
            <div className="text-[10px] text-muted-foreground -mt-0.5 truncate group-hover:text-mint transition">
              Click for new chat
            </div>
          </div>
        </button>
        {onCloseMobile && (
          <button
            onClick={onCloseMobile}
            data-testid="close-mobile-sidebar"
            className="md:hidden w-7 h-7 rounded-md hover:bg-secondary flex items-center justify-center text-muted-foreground"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Guru dropdown - compact */}
      <div className="px-3 pb-2">
        <div className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground mb-1 px-1 flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5 text-mint" /> Select Your Guru
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="guru-dropdown-trigger"
              className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-secondary/60 border border-border hover:border-mint/60 transition group"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <BookOpen className="w-3.5 h-3.5 text-mint shrink-0" />
                <span className="text-[12.5px] font-medium truncate">{guru.name}</span>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground group-hover:text-mint transition" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[256px]">
            {GURUS.map(g => (
              <DropdownMenuItem
                key={g.id}
                data-testid={`guru-option-${g.id}`}
                disabled={!g.live}
                onClick={() => g.live && onGuruChange(g.id)}
                className="flex-col items-start gap-0.5 py-2"
              >
                <div className="w-full flex items-center justify-between">
                  <span className="text-[12.5px] font-medium">{g.name}</span>
                  {!g.live && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Soon</span>}
                </div>
                <span className="text-[10.5px] text-muted-foreground">{g.desc}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Diary quick action - compact */}
      <div className="px-3 pb-2">
        <button
          data-testid="open-diary-btn"
          onClick={onOpenDiary}
          className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-transparent border border-border hover:border-bblue/60 hover:bg-secondary/40 transition group"
        >
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-md bg-bblue/15 text-bblue flex items-center justify-center">
              <NotebookPen className="w-3 h-3" />
            </div>
            <div className="text-left leading-tight">
              <div className="text-[12px] font-medium">My Diary</div>
              <div className="text-[9.5px] text-muted-foreground">Notes & exports</div>
            </div>
          </div>
          <Plus className="w-3 h-3 text-muted-foreground group-hover:text-bblue transition" />
        </button>
      </div>

      {/* Recent chats header */}
      <div className="px-3 pt-1 pb-1.5 flex items-center justify-between">
        <span className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground">Recent Chats</span>
        <button
          data-testid="new-chat-btn"
          onClick={onNewChat}
          className="w-6 h-6 rounded-md bg-mint/15 text-mint hover:bg-mint/25 transition flex items-center justify-center"
          aria-label="New chat"
          title="New chat"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      <ScrollArea className="flex-1 px-2">
        {chats.length === 0 ? (
          <div data-testid="empty-chats" className="mx-2 mt-2 p-3 rounded-xl border border-dashed border-border text-center">
            <div className="text-[12px] text-foreground/80 leading-snug">No chats yet.</div>
            <div className="text-[10.5px] text-muted-foreground mt-0.5">Start a new conversation!</div>
            <button
              onClick={onNewChat}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-mint hover:underline"
              data-testid="empty-chats-new"
            >
              <Plus className="w-3 h-3" /> New chat
            </button>
          </div>
        ) : (
          <ul className="space-y-0.5 pr-1">
            {chats.map(c => (
              <li key={c.id}>
                <button
                  data-testid={`chat-item-${c.id}`}
                  onClick={() => onSelectChat(c.id)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg text-[12.5px] transition border ${
                    activeChatId === c.id
                      ? 'bg-secondary border-mint/40 text-foreground'
                      : 'border-transparent text-foreground/80 hover:bg-secondary/60 hover:text-foreground'
                  }`}
                >
                  <div className="truncate font-medium">{c.title || 'Untitled chat'}</div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    {c.messages?.length || 0} messages
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>

      {/* Gamification widget */}
      <div className="mx-3 mb-2 p-2.5 rounded-xl bg-secondary/60 border border-border" data-testid="gami-widget">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-md bg-mint/15 text-mint flex items-center justify-center">
              <Trophy className="w-3 h-3" />
            </div>
            <div className="leading-tight">
              <div className="text-[11px] font-semibold">Level {level}</div>
              <div className="text-[9.5px] text-muted-foreground">{currentXP}/{nextLevelXP} XP</div>
            </div>
          </div>
          <span className="text-[9.5px] text-mint font-medium">+{gami?.xp || 0}</span>
        </div>
        <Progress value={pct} className="h-1 mt-1.5 bg-background" />
      </div>

      {/* Quota widget */}
      <QuotaWidget onUpgrade={onOpenPremium} />

      {/* Settings */}
      <div className="px-3 pb-3">
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2 border-border hover:border-mint/40 h-8 text-[12px]"
          onClick={onOpenSettings}
          data-testid="open-settings-btn"
        >
          <Settings className="w-3.5 h-3.5" />
          Settings
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
